import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Smartphone, 
  QrCode, 
  BarChart3, 
  Users, 
  Zap,
  ArrowRight,
  Check,
  CreditCard,
  Box,
  Circle,
  Menu,
  X
} from 'lucide-react';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'NFC & QR Networking',
      description: 'Share your digital profile with a tap or scan. Works with cards, rings, bracelets, and more.',
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Real-time Analytics',
      description: 'Track every tap, view, and lead. Understand what resonates with your network.',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Team Management',
      description: 'Manage your entire sales team from one dashboard. Track performance across the organization.',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Lead Capture',
      description: 'Capture leads directly from your profile page. Never lose a connection again.',
    },
  ];

  const artifacts = [
    { name: 'NFC Cards', icon: <CreditCard className="w-8 h-8" />, color: 'bg-slate-900' },
    { name: 'QR Cubes', icon: <Box className="w-8 h-8" />, color: 'bg-indigo-600' },
    { name: 'Smart Rings', icon: <Circle className="w-8 h-8" />, color: 'bg-emerald-600' },
  ];

  const plans = [
    {
      name: 'Starter Kit',
      price: '₱1,299',
      period: ' one-time',
      caption: 'Best for individuals getting started with smart networking.',
      features: ['2 Smart Networking Artifacts', '1 Profile', 'Lead capture', 'Basic analytics', '1 Video upload', '1 PDF upload', 'Signa hosted profile'],
      buttonText: 'Buy Starter Kit',
      buttonLink: '/shop',
    },
    {
      name: 'Pro',
      price: '₱500',
      period: ' / user / month',
      caption: 'Best for sales professionals and growing teams.',
      features: ['6 Profiles', 'Unlimited artifacts', 'Workflow funnels', 'Campaign artifact tracking', 'Lead-gated downloads', 'Advanced analytics', 'Customer assignment', 'Profile scheduling', 'Custom domains', 'Remove Signa branding'],
      popular: true,
      buttonText: 'Upgrade to Pro',
      buttonLink: '/register',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      caption: 'Best for companies and large organizations.',
      features: ['White-label deployment', 'API access', 'SLA', 'Priority support', 'Bulk artifact provisioning', 'Organization analytics'],
      buttonText: 'Contact Sales',
      buttonLink: '#',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Signa
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Features</a>
              <Link to="/shop" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Artifacts</Link>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Pricing</a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-btn">Log in</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="get-started-btn">
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-slate-200">
            <div className="px-4 py-4 space-y-4">
              <a href="#features" className="block text-slate-600 hover:text-slate-900 font-medium">Features</a>
              <Link to="/shop" className="block text-slate-600 hover:text-slate-900 font-medium">Artifacts</Link>
              <a href="#pricing" className="block text-slate-600 hover:text-slate-900 font-medium">Pricing</a>
              <div className="pt-4 border-t border-slate-200 space-y-3">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">Log in</Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="w-full bg-indigo-600 hover:bg-indigo-700">Get Started</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Transform your networking
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Turn every tap into a{' '}
                <span className="text-indigo-600">trackable lead</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl">
                Signa converts offline networking into digital lead funnels. Share your profile with NFC cards, QR codes, 
                and smart devices while tracking every interaction.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto" data-testid="hero-cta-btn">
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    See how it works
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative animate-slide-up">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-indigo-100 to-slate-100 p-8 flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1752159400890-d906038f1b35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMG5ldHdvcmtpbmclMjBldmVudCUyMHNoYWtpbmclMjBoYW5kc3xlbnwwfHx8fDE3NzI2NzEyMDN8MA&ixlib=rb-4.1.0&q=85"
                  alt="Business networking"
                  className="rounded-xl shadow-2xl object-cover w-full h-full"
                />
              </div>
              {/* Floating stats card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-200 animate-scale-in">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">2,847</p>
                    <p className="text-sm text-slate-500">Leads captured today</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything you need to grow your network
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From individual professionals to enterprise sales teams, Signa scales with your networking needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Artifacts Section */}
      <section id="artifacts" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                One profile, countless ways to share
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Connect your digital profile to physical networking artifacts. Each tap or scan is tracked, 
                giving you insights into how your network grows.
              </p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                {artifacts.map((artifact, index) => (
                  <div
                    key={index}
                    className="text-center p-4 rounded-xl border border-slate-200 hover:border-indigo-300 transition-colors"
                    data-testid={`artifact-${index}`}
                  >
                    <div className={`w-16 h-16 ${artifact.color} rounded-xl mx-auto mb-3 flex items-center justify-center text-white`}>
                      {artifact.icon}
                    </div>
                    <p className="text-sm font-medium text-slate-700">{artifact.name}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <QrCode className="w-6 h-6 text-slate-400" />
                <p className="text-sm text-slate-500">
                  Also supports pendants, bracelets, keychains, and more
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1628891439478-c613e85af7d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYmxhY2slMjBtZXRhbCUyMGNyZWRpdCUyMGNhcmR8ZW58MHx8fHwxNzcyNjcxMjA2fDA&ixlib=rb-4.1.0&q=85"
                alt="NFC Card"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get started with a Starter Kit or unlock the full platform with Pro.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl border-2 p-8 relative ${
                  plan.popular ? 'border-indigo-600 shadow-lg' : 'border-slate-200'
                }`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {plan.name}
                </h3>
                <p className="text-sm text-slate-500 mb-4">{plan.caption}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  {plan.period && <span className="text-slate-500 text-sm">{plan.period}</span>}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600 text-sm">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to={plan.buttonLink}>
                  <Button
                    className={`w-full ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    data-testid={`pricing-btn-${index}`}
                  >
                    {plan.buttonText}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to transform your networking?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Join thousands of professionals who have upgraded their networking game with Signa.
          </p>
          <Link to="/shop">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700" data-testid="cta-bottom-btn">
              Browse Artifacts
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-white text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Signa
              </span>
            </div>
            <div className="flex items-center gap-8">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <Link to="/shop" className="hover:text-white transition-colors">Shop</Link>
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
            </div>
            <p className="text-sm">© 2026 Signa. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
